import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import style from "./style.module.css";
import { IoClose, IoSearchOutline } from "react-icons/io5";
import { FaUser } from "react-icons/fa";
import UserProfilePopup from "../userProflePopup/UserProfilePopup";
import { getGlobalSearch } from "../../api/HttpApis";
import { useSearch } from "../../context/SearchContext";
import { useTenant } from "../../context/TenantContext";
import { debounce } from "../../utils/debounce";

const HIDDEN_SEARCH_ROUTES = [
  "/mainlayout/submissions",
  "/mainlayout/bulk-upload",
  "/mainlayout/ad-management",
  "/mainlayout/subscription",
  "/mainlayout/experience-builder",
  "/mainlayout/analytics-dashboard",
  "/mainlayout/settings",
];

const Searchbar = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false); 
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const searchNavigated = useRef(false);
  const [localResults, setLocalResults] = useState([]);
  const { hasFeature } = useTenant();
  // Creator results open the creator list, which needs the content partners portal.
  const visibleResults = hasFeature("content_partners_portal")
    ? localResults
    : localResults.filter((item) => item?.content_type !== "Creator");
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef(null);
  const { searchQuery, setSearchQuery } = useSearch("");

  const latestSearchId = useRef(0);
  const handleUserClick = () => {
    setIsPopupOpen(!isPopupOpen);
  };

  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const endpoint = pathSegments[pathSegments.length - 1];
  const endpoint2 = pathSegments[pathSegments.length - 2];

  const isSearchHidden = HIDDEN_SEARCH_ROUTES.some((r) =>
    location.pathname.startsWith(r)
  );

  useEffect(() => {
    if (!isSearchHidden && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSearchHidden, location.pathname]);

  const routeMap = {
    Movie: "/mainlayout/movie",
    Podcast: "/mainlayout/podcasts",
    "Short Clip": "/mainlayout/short-clips",
    Series: "/mainlayout/series",
    "Live Stream": "/mainlayout/live-streams",
    "Live Channel": "/mainlayout/live-tv-channels",
    Creator: "/mainlayout/creator-list",
  };

  const previousPathRef = useRef(location.pathname);

  const performSearch = useCallback(
    async (value) => {
      setLocalResults([]);
      const searchId = ++latestSearchId.current;

      const excludedEndpoints = [
        "movie",
        "series",
        "podcasts",
        "short-clips",
        "live-tv-channels",
        "live-streams",
        "seasons",
        "episodes",
        "creator-list",
        "users",
      ];

      if (
        !excludedEndpoints.includes(endpoint) &&
        !excludedEndpoints.includes(endpoint2)
      ) {
        try {
          const response = await getGlobalSearch(value);

          if (
            searchId === latestSearchId.current &&
            response?.data?.status === "success" &&
            Array.isArray(response.data?.data)
          ) {
            if (!searchQuery) {
              setLocalResults([]);
            }
            if (value.length > 0) {
              setLocalResults(response?.data?.data || []);
              setHasSearched(true);
              setShowDropdown(true);
            }
          } else if (searchId === latestSearchId.current) {
            setLocalResults([]);
            setHasSearched(true);
            setShowDropdown(true);
          }
        } catch (error) {
          if (searchId === latestSearchId.current) {
            setLocalResults([]);
            setHasSearched(true);
            setShowDropdown(true);
          }
        }
      }
    },
    [endpoint]
  );

  const debouncedSearch = useMemo(
    () => debounce(performSearch, 500),
    [performSearch]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (!e.target.value) {
      setShowDropdown(false);
      setLocalResults([]);
      setHasSearched(false);
    }

    if (!value.trim()) {
      setLocalResults([]);
      setShowDropdown(false);
      setHasSearched(false);
      debouncedSearch.cancel();
      return;
    }

    debouncedSearch(value);
  };

  const handleResultClick = (item) => {
    const { content_type, id, name } = item;

    setSearchQuery(name || "");
    setShowDropdown(false);

    if (content_type === "Series" && id) {
      navigate(`/Addseries/${id}`);
      return;
    }
    if (content_type && id) {
      navigate(`/video/${id}`);
      setSearchQuery("");
      return;
    }

    const targetRoute = routeMap[content_type];
    if (targetRoute) {
      searchNavigated.current = true;
      navigate(targetRoute);
    }
  };

  useEffect(() => {
    const prevPath = previousPathRef.current;
    const currentPath = location.pathname;

    if (prevPath !== currentPath) {
      setSearchQuery("");
      setLocalResults([]);
      setShowDropdown(false);
      setHasSearched(false);
      searchNavigated.current = false;
    }

    previousPathRef.current = currentPath;
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleClose = () => {
    setSearchQuery("");
    setShowDropdown(false);
  };
  return (
    <>
      <div className={style.mainContainer} ref={searchRef}>
        <div className={style.searchbarContainer}>
          {!isSearchHidden && (
            <div className={style.searchField}>
              <IoSearchOutline className={style.searchIconInline} />
              <input
                ref={inputRef}
                className={style.searchText}
                type="text"
                placeholder="Search..."
                value={searchQuery || ""}
                onChange={handleChange}
                autoComplete="off"
              />
              {searchQuery ? (
                <button
                  type="button"
                  className={style.clearBtn}
                  onClick={handleClose}
                  aria-label="Clear search"
                >
                  <IoClose className={style.clearIconSize} />
                </button>
              ) : null}

              {showDropdown && searchQuery.length > 0 && hasSearched && (
                <div className={style.dropdown}>
                  {visibleResults?.length > 0 ? (
                    visibleResults.map((item) => (
                      <div
                        key={item?.id}
                        className={style.dropdownItem}
                        onClick={() => handleResultClick(item)}
                      >
                        <img
                          src={item?.portrait}
                          alt={item?.name}
                          className={style.dropdownImage}
                        />
                        <div className={style.nameText}>
                          <span>{item?.name}</span>
                          <span className={style.contentType}>
                            {item?.content_type}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={style.dropdownItem}>
                      <div className={style.nameText}>
                        <span>No results found</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            className={style.userIcon}
            onClick={handleUserClick}
            aria-label="Account"
          >
            <FaUser className={style.userIconSize} />
          </button>
          <UserProfilePopup
            isOpen={isPopupOpen}
            onClose={() => setIsPopupOpen(false)}
          />
        </div>
      </div>
    </>
  );
};

export default Searchbar;
